import React, {useCallback, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {clearScheduledDate} from 'coreComponents/DevToolkit/constants';
import {useContentMgr, useEntryInspectorToggle} from 'coreHooks';
import {ECMSMode} from 'coreStore/data/configurations/types';
import {getCpaConnectionInfo, isEntryInspectorEnabled} from 'coreUtils/livePreview';

import CPAConnectionSection from '../CPAConnectionSection';
import {clearCpaConnection} from '../CPAConnectionSection/api';

import {ICMSPreviewSectionProps} from './ICMSPreviewSection';
import messages from './messages';

import styles from './CMSPreviewSection.module.scss';

const CMSPreviewSection = ({togglePanel}: ICMSPreviewSectionProps) => {
	const intl = useIntl();
	const contentMgr = useContentMgr();
	const initialValue = contentMgr.isPreviewMode() ? ECMSMode.PREVIEW : ECMSMode.LIVE;
	const [selectedMode, setSelectedMode] = useState<ECMSMode>(initialValue);
	const [isInspectorOn, setIsInspectorOn] = useEntryInspectorToggle();
	const showInspectorSwitch = contentMgr.isPreviewMode() && isEntryInspectorEnabled();

	const handleApply = useCallback(async () => {
		contentMgr.changeMode(selectedMode);

		if (selectedMode !== ECMSMode.PREVIEW) {
			clearScheduledDate();

			if (getCpaConnectionInfo()) {
				await clearCpaConnection();
			}
		}

		togglePanel();
		window.location.reload();
	}, [contentMgr, selectedMode, togglePanel]);

	return (
		<div className={styles['cms-section']}>
			<label className={styles['mode-label']}>
				{intl.formatMessage(messages.modeLabel)}
			</label>
			<div className={styles['mode-segmented']}>
				<button
					type="button"
					className={cn(styles['mode-btn'], {
						[styles['mode-btn--active']]: selectedMode === ECMSMode.LIVE,
					})}
					onClick={() => setSelectedMode(ECMSMode.LIVE)}
				>
					<span className={cn(styles['mode-dot'], styles['mode-dot--live'])} />
					{intl.formatMessage(messages.modeLive)}
				</button>
				<button
					type="button"
					className={cn(styles['mode-btn'], {
						[styles['mode-btn--active']]: selectedMode === ECMSMode.PREVIEW,
					})}
					onClick={() => setSelectedMode(ECMSMode.PREVIEW)}
				>
					<span className={cn(styles['mode-dot'], styles['mode-dot--preview'])} />
					{intl.formatMessage(messages.modePreview)}
				</button>
			</div>
			{showInspectorSwitch && (
				<label className={styles['inspector-switch']}>
					<span className={styles['inspector-switch__text']}>
						<span className={styles['inspector-switch__label']}>
							{intl.formatMessage(messages.entryInspectorLabel)}
						</span>
						<span className={styles['inspector-switch__hint']}>
							{intl.formatMessage(messages.entryInspectorHint)}
						</span>
					</span>
					<input
						type="checkbox"
						role="switch"
						className={styles['inspector-switch__input']}
						checked={isInspectorOn}
						onChange={(e) => setIsInspectorOn(e.target.checked)}
					/>
				</label>
			)}
			<button
				type="button"
				className={styles['apply-button']}
				onClick={handleApply}
			>
				{intl.formatMessage(messages.apply)}
			</button>
			{selectedMode === ECMSMode.PREVIEW && <CPAConnectionSection onApplied={togglePanel} />}
		</div>
	);
};

export default CMSPreviewSection;
